import React, { useState, useEffect } from 'react';
import { Container, Tabs, Tab, Table, Button, Form, Row, Col, Badge, Card, Spinner, InputGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import { FaCheck, FaTimes, FaSearch, FaTrash } from 'react-icons/fa';

const Inventory = () => {
    const [key, setKey] = useState('list');

    return (
        <Container fluid className="p-4">
            <h2 className="mb-4">Inventarizatsiya</h2>
            <Tabs
                id="inventory-tabs"
                activeKey={key}
                onSelect={(k) => setKey(k)}
                className="mb-3"
            >
                <Tab eventKey="list" title="Ro'yxat">
                    <InventoryList />
                </Tab>
                <Tab eventKey="stocktaking" title="Sanoq (Stocktaking)">
                    <Stocktaking onInventoryCreated={() => setKey('list')} />
                </Tab>
            </Tabs>
        </Container>
    );
};

const InventoryList = () => {
    const [inventories, setInventories] = useState([]);
    const [loading, setLoading] = useState(true);
    const { token, user } = useAuth();
    const [expandedRow, setExpandedRow] = useState(null);
    const [details, setDetails] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const fetchInventories = async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/inventory', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            setInventories(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInventories();
    }, []);

    const fetchDetails = async (id) => {
        setLoadingDetails(true);
        try {
            const res = await fetch(`/api/inventory/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('Failed to fetch details');
            const data = await res.json();
            setDetails(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoadingDetails(false);
        }
    };

    const handleRowClick = (id) => {
        if (expandedRow === id) {
            setExpandedRow(null);
            setDetails(null);
        } else {
            setExpandedRow(id);
            fetchDetails(id);
        }
    };

    const handleAction = async (id, action) => {
        if (!window.confirm(`Are you sure you want to ${action} this inventory?`)) return;
        try {
            const res = await fetch(`/api/inventory/${id}/${action}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.message);
            }
            toast.success(`Inventory ${action}ed`);
            fetchInventories();
            if (expandedRow === id) fetchDetails(id);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const getStatusBadge = (status) => {
        if (status === 0) return <Badge bg="primary">Yangi</Badge>;
        if (status === 1) return <Badge bg="success">Tasdiqlangan</Badge>;
        if (status === 2) return <Badge bg="danger">Rad etilgan</Badge>;
        return <Badge bg="secondary">{status}</Badge>;
    };

    if (loading) return <Loader />;

    return (
        <Card>
            <Card.Body>
                <Table hover responsive striped>
                    <thead>
                        <tr>
                            <th>Sana</th>
                            <th>Bajaruvchi</th>
                            <th>Items Count</th>
                            <th>Yo'qotish (Summa)</th>
                            <th>Status</th>
                            {user.role === 'owner' && <th>Amallar</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {inventories.map(inv => (
                            <React.Fragment key={inv.id}>
                                <tr
                                    onClick={() => handleRowClick(inv.id)}
                                    style={{ cursor: 'pointer', backgroundColor: expandedRow === inv.id ? '#f8f9fa' : 'inherit' }}
                                >
                                    <td>{new Date(inv.date).toLocaleString()}</td>
                                    <td>{inv.doer_name}</td>
                                    <td>{inv.items_count}</td>
                                    <td>
                                        {inv.lost_totals && inv.lost_totals.length > 0 ? (
                                            inv.lost_totals.map((t, idx) => (
                                                <div key={idx}>{parseFloat(t.total).toLocaleString()} {t.currency}</div>
                                            ))
                                        ) : '0'}
                                    </td>
                                    <td>{getStatusBadge(inv.status)}</td>
                                    {user.role === 'owner' && (
                                        <td onClick={(e) => e.stopPropagation()}>
                                            {inv.status === 0 && (
                                                <>
                                                    <Button variant="success" size="sm" className="me-2" onClick={() => handleAction(inv.id, 'confirm')}><FaCheck /></Button>
                                                    <Button variant="danger" size="sm" onClick={() => handleAction(inv.id, 'reject')}><FaTimes /></Button>
                                                </>
                                            )}
                                        </td>
                                    )}
                                </tr>
                                {expandedRow === inv.id && (
                                    <tr>
                                        <td colSpan={6}>
                                            {loadingDetails ? <Spinner size="sm" animation="border" /> : details && (
                                                <div className="p-3 bg-light border rounded">
                                                    <h5>Tafsilotlar ({details.items.length} ta mahsulot)</h5>
                                                    <Table size="sm" bordered>
                                                        <thead>
                                                            <tr>
                                                                <th>Mahsulot</th>
                                                                <th>Oldingi Qoldiq</th>
                                                                <th>Haqiqiy Qoldiq</th>
                                                                <th>Farq</th>
                                                                <th>Yo'qotish Summasi</th>
                                                                <th>Valyuta</th>
                                                                <th>Barcode</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {details.items.map(item => (
                                                                <tr key={item.id}>
                                                                    <td>{item.product_name}</td>
                                                                    <td>{item.former_quantity}</td>
                                                                    <td>{item.actual_quantity}</td>
                                                                    <td className={item.former_quantity - item.actual_quantity > 0 ? "text-danger" : "text-success"}>
                                                                        {(item.former_quantity - item.actual_quantity).toFixed(2)}
                                                                    </td>
                                                                    <td>{parseFloat(item.lost_price).toLocaleString()}</td>
                                                                    <td>{item.currency}</td>
                                                                    <td>{item.barcode || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                    <div className="mt-2 text-muted">
                                                        <strong>Izoh:</strong> {inv.note || 'Yo\'q'}
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    );
};

const Stocktaking = ({ onInventoryCreated }) => {
    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [cart, setCart] = useState([]); // List of inventory items ready to submit
    const [loadingSearch, setLoadingSearch] = useState(false);
    const { token, user } = useAuth();
    const [note, setNote] = useState('');

    // FIFO Batch Inputs
    const [actualQuantities, setActualQuantities] = useState({}); // { batchId: value }

    // Specific Batch Selections
    const [selectedBatchIds, setSelectedBatchIds] = useState({}); // { batchId: true/false }

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoadingSearch(true);
        try {
            const res = await fetch(`/api/inventory/products?query=${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            setSearchResults(data);
            setSelectedProduct(null);
        } catch (err) {
            toast.error('Search failed');
        } finally {
            setLoadingSearch(false);
        }
    };

    const handleSelectProduct = (item) => {
        setSelectedProduct(item);
        // Reset inputs for this product
        setActualQuantities({});
        // For Specific, pre-select? No, user must select found ones. Or select all?
        // User said: "We just select the product... display only purchase_batches which are availabe... After finding, we just select the product"
        // I'll default to empty selection (Found nothing).
        setSelectedBatchIds({});
    };

    const handleAddToSession = () => {
        // Validation and Calculation
        const newItems = [];

        selectedProduct.batches.forEach(batch => {
            let actualQty = 0;
            let formerQty = parseFloat(batch.quantity_remaining);
            let isSpecific = !!batch.barcode;

            // Process batch
            if (isSpecific) {
                if (selectedBatchIds[batch.id]) {
                    // Marked as Missing/Write-off
                    newItems.push({
                        product_id: selectedProduct.product.productid,
                        purchase_batch_id: batch.id,
                        former_quantity: 1,
                        actual_quantity: 0,
                        lost_price: batch.costperunit * 1,
                        currency: batch.currency || 'USD',
                        owner_id: user.ownerId,
                        barcode: batch.barcode,
                        name: selectedProduct.product.name // For display
                    });
                }
            } else {
                // FIFO
                // If input exists
                if (actualQuantities[batch.id] !== undefined) {
                    const actualValue = actualQuantities[batch.id];
                    if (actualValue === '') return; // Skip if empty

                    const actual = parseFloat(actualValue);
                    const diff = formerQty - actual;

                    newItems.push({
                        product_id: selectedProduct.product.productid,
                        purchase_batch_id: batch.id,
                        former_quantity: formerQty,
                        actual_quantity: actual,
                        lost_price: batch.costperunit * diff,
                        currency: batch.currency || 'USD',
                        owner_id: user.ownerId,
                        barcode: null,
                        name: selectedProduct.product.name
                    });
                }
            }
        });

        setCart([...cart, ...newItems]);
        setSelectedProduct(null);
        setSearchResults([]);
        setQuery('');
    };

    const handleRemoveFromCart = (idx) => {
        const newCart = [...cart];
        newCart.splice(idx, 1);
        setCart(newCart);
    };

    const handleSubmitInventory = async () => {
        if (cart.length === 0) return;
        if (!window.confirm('Confirm creating inventory?')) return;

        try {
            const res = await fetch('/api/inventory', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    date: new Date(),
                    note: note,
                    items: cart
                })
            });
            if (!res.ok) throw new Error('Failed');
            toast.success('Inventory created!');
            setCart([]);
            setNote('');
            onInventoryCreated();
        } catch (err) {
            toast.error('Error creating inventory');
        }
    };

    // Calculate total lost by currency
    const totalsByCurrency = cart.reduce((acc, item) => {
        const curr = item.currency || 'USD';
        acc[curr] = (acc[curr] || 0) + (parseFloat(item.lost_price) || 0);
        return acc;
    }, {});

    return (
        <Row>
            <Col md={selectedProduct ? 4 : 12}>
                <Card className="mb-3">
                    <Card.Body>
                        <h5>Mahsulot Qidirish</h5>
                        <Form onSubmit={handleSearch}>
                            <InputGroup className="mb-3">
                                <Form.Control
                                    placeholder="Nom yoki Barcode..."
                                    value={query}
                                    onChange={e => setQuery(e.target.value)}
                                />
                                <Button type="submit" variant="primary">
                                    {loadingSearch ? <Spinner size="sm" /> : <FaSearch />}
                                </Button>
                            </InputGroup>
                        </Form>
                        {!selectedProduct && searchResults.length > 0 && (
                            <div className="list-group">
                                {searchResults.map(item => (
                                    <button
                                        key={item.product.productid}
                                        className="list-group-item list-group-item-action"
                                        onClick={() => handleSelectProduct(item)}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <strong>{item.product.name}</strong>
                                            <Badge bg="secondary">{item.batches.length} batches</Badge>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </Card.Body>
                </Card>

                {/* Cart / Draft */}
                <Card>
                    <Card.Header>Qoralama (Draft) - {cart.length} items</Card.Header>
                    <Card.Body>
                        {cart.length > 0 ? (
                            <Table size="sm" fontSize="12px">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Act.</th>
                                        <th>Yo'qotish</th>
                                        <th>Del</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cart.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{item.name}</td>
                                            <td>{item.actual_quantity}</td>
                                            <td>{item.lost_price.toLocaleString()} {item.currency}</td>
                                            <td><FaTrash className="text-danger pointer" onClick={() => handleRemoveFromCart(idx)} /></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : <p className="text-muted text-center">Bo'sh</p>}

                        <div className="mt-2">
                            <strong>Jami Yo'qotish: </strong>
                            {Object.entries(totalsByCurrency).map(([curr, total]) => (
                                <div key={curr} className="ms-3">{total.toLocaleString()} {curr}</div>
                            ))}
                            {Object.keys(totalsByCurrency).length === 0 && '0'}
                        </div>
                        <Form.Group className="mt-2">
                            <Form.Label>Izoh</Form.Label>
                            <Form.Control as="textarea" rows={2} value={note} onChange={e => setNote(e.target.value)} />
                        </Form.Group>
                        <Button className="w-100 mt-2" variant="success" disabled={cart.length === 0} onClick={handleSubmitInventory}>
                            Inventarizatsiyani Yaratish
                        </Button>
                    </Card.Body>
                </Card>
            </Col>

            {selectedProduct && (
                <Col md={8}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5>{selectedProduct.product.name} - Partiyalar</h5>
                            <Button variant="secondary" size="sm" onClick={() => setSelectedProduct(null)}>Yopish</Button>
                        </Card.Header>
                        <Card.Body>
                            <Table striped bordered>
                                <thead>
                                    <tr>
                                        <th>Sana</th>
                                        <th>Ombor</th>
                                        <th>Qoldiq</th>
                                        <th>Narx (Olish)</th>
                                        {selectedProduct.batches.some(b => b.barcode) ? (
                                            <>
                                                <th>Barcode</th>
                                                <th>Yo'qolgan/O'chirish</th>
                                            </>
                                        ) : (
                                            <>
                                                <th>Haqiqiy Qoldiq (Sanoq)</th>
                                                <th>Farq</th>
                                                <th>Yo'qotish Summasi</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedProduct.batches.map(batch => {
                                        const isSpecific = !!batch.barcode;
                                        const former = parseFloat(batch.quantity_remaining);
                                        // Calculations for FIFO
                                        const actual = parseFloat(actualQuantities[batch.id] !== undefined ? actualQuantities[batch.id] : former); // Default to former if untouched? Or 0? Usually default to "Existing" is safer for "Adjustments", but for "Count" default 0 is safer? 
                                        // If I default to former, user thinks "It's correct".
                                        // Let's default to empty string in UI loop, handling logic below.

                                        const diff = former - actual;
                                        const lost = diff * batch.costperunit;

                                        return (
                                            <tr key={batch.id}>
                                                <td>{new Date(batch.created_at).toLocaleDateString()}</td>
                                                <td>{batch.store_name}</td>
                                                <td>{former}</td>
                                                <td>{parseFloat(batch.costperunit).toLocaleString()} {batch.currency}</td>

                                                {isSpecific ? (
                                                    <>
                                                        <td>{batch.barcode}</td>
                                                        <td className="text-center">
                                                            <Form.Check
                                                                type="checkbox"
                                                                label="Yo'qolgan (O'chirish)"
                                                                className="text-danger"
                                                                checked={!!selectedBatchIds[batch.id]}
                                                                onChange={e => setSelectedBatchIds({ ...selectedBatchIds, [batch.id]: e.target.checked })}
                                                            />
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                value={actualQuantities[batch.id] !== undefined ? actualQuantities[batch.id] : ''}
                                                                placeholder={former}
                                                                onChange={e => setActualQuantities({ ...actualQuantities, [batch.id]: e.target.value })}
                                                            />
                                                        </td>
                                                        <td>{actualQuantities[batch.id] !== undefined ? diff.toFixed(2) : '-'}</td>
                                                        <td>{actualQuantities[batch.id] !== undefined ? `${lost.toLocaleString()} ${batch.currency}` : '-'}</td>
                                                    </>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            <Button variant="primary" className="mt-3 float-end" onClick={handleAddToSession}>
                                Qo'shish & Davom etish
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            )}
        </Row>
    );
};

export default Inventory;
